/**
 * Alliance 영역 데이터 접근 계층
 *
 * - 현재: 서버 API 기반 (일부 화면은 데이터 미구현 시 빈 값 반환)
 */

import Http from '../../../../../core/Http';
import {
  ALLIANCE_APPROVAL_STATUS,
  ALLIANCE_LIST_LIFECYCLE,
  ALLIANCE_MOCK_COUNCIL_PROGRESS,
  ALLIANCE_STAGE_CD,
  normalizeAllianceOperationStatusCd,
} from '../domain/allianceUiCodes';

const DEFAULT_SPACE_TYPE = 'tech-demand';

const DEFAULT_COUNCIL_BADGE_BY_PROGRESS = Object.freeze({
  [ALLIANCE_MOCK_COUNCIL_PROGRESS.IN_PROGRESS]: 'Alliance',
  [ALLIANCE_MOCK_COUNCIL_PROGRESS.WAITING]: 'Alliance',
  [ALLIANCE_MOCK_COUNCIL_PROGRESS.ENDED]: '종료',
  [ALLIANCE_MOCK_COUNCIL_PROGRESS.CLOSED]: '폐쇄',
});

function normalizeCouncilProgressCd(item) {
  const raw = item?.progressStatusCd ?? item?.status;
  const u = String(raw || '').trim().toUpperCase();
  const allowed = Object.values(ALLIANCE_MOCK_COUNCIL_PROGRESS);
  if (allowed.includes(u)) return u;
  return ALLIANCE_MOCK_COUNCIL_PROGRESS.IN_PROGRESS;
}

/** 400 access denied / 인증 관련 응답은 상위에서 처리하도록 그대로 전달 */
function shouldRethrowSpaceFetchError(status, msgLower) {
  if (status === 400 && msgLower.includes('alliance space access denied')) return true;
  if (status === 401 || status === 403) return true;
  return /\b(login|unauthorized|unauthenticated|authentication required)\b/.test(msgLower);
}

/** GLOW_ALLIANCE_SPACE.END_AT 기준 — 종료일이 오늘(로컬) 이전이면 ENDED (폐쇄 CLOSED는 별도 API 필드 도입 시 확장) */
function listLifecycleCdFromAllianceEndAt(endAtRaw) {
  const d = String(endAtRaw || '').trim().slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(d)) return undefined;
  const today = new Date();
  const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
  if (d < todayStr) return ALLIANCE_LIST_LIFECYCLE.ENDED;
  return undefined;
}

/**
 * 스페이스 유형별 전체 데이터 (대시보드·마일스톤 탭 등에서 사용)
 * @param {string} [spaceType] - 'pre-alliance' | 'tech-demand' | 'research'
 * @returns 동일 shape 객체 (milestoneTabItems, milestoneDetail.projectById, milestoneDetail.detailById, milestoneDetail.repliesByDetailId, statusCards, overviewNotices 등)
 */
export function getSpaceData(spaceType = DEFAULT_SPACE_TYPE) {
  // NOTE: 과거 _mocks 기반 동기 데이터. 현재는 목업 제거로 인해 빈 shape를 반환한다.
  // 필요한 경우 서버 API로 대체 구현한다.
  return {
    spaceType,
    spaceMembers: [],
    milestoneTabItems: [],
    milestoneDetail: {
      projectById: {},
      detailById: {},
      repliesByDetailId: {},
    },
    statusCards: [],
    overviewNotices: [],
    taskInfo: null,
    cooperation: null,
  };
}

/**
 * 현재 Alliance Space 참석자(구성원) 목록 - 일정 팝업 참석자 선택용
 * @param {string} [spaceType] - 'pre-alliance' | 'tech-demand' | 'research'
 * @returns Array<{ id, name, org, email? }> - org에는 "(주)회사명" 등 괄호 포함 가능
 */
export function getSpaceMembers(spaceType = DEFAULT_SPACE_TYPE) {
  const data = getSpaceData(spaceType);
  return Array.isArray(data?.spaceMembers) ? [...data.spaceMembers] : [];
}

/**
 * PoC 목록 (Alliance 진입 전 스페이스 목록)
 * @returns Array<{ id, title, spaceType, ... }>
 */
export function getPocList() {
  return [];
}

/** 목업 제목에서 "Alliance Space 날짜-인덱싱넘버", "Pre-Alliance", "Pre-Poc Room" 제거 */
function cleanAllianceTitle(raw) {
  if (!raw || typeof raw !== 'string') return '';
  return raw
    .replace(/\s*Alliance Space\s+\d+-\d+\s*$/, '')
    .replace(/\s*Pre-Poc Room\s*$/i, '')
    .replace(/\s*Pre-Alliance\s*$/i, '')
    .trim();
}

/** 날짜 문자열(YYYY.MM.DD 또는 YYYY-MM-DD) → "YYYY년 MM월 DD일" */
function formatOpenedAt(dateStr) {
  if (!dateStr || typeof dateStr !== 'string') return '';
  const normalized = String(dateStr).replace(/\./g, '-').trim();
  const [y, m, d] = normalized.split(/[-/]/);
  if (!y || !m) return dateStr;
  const month = m.padStart(2, '0');
  const day = (d && d.padStart(2, '0')) || '01';
  return `${y}년 ${month}월 ${day}일`;
}

/** 날짜+시간 → "YYYY년 MM월 DD일 HH:MM" (목업용 기본 최근 업데이트 표시) */
function formatUpdatedAt(dateStr, timeStr = '16:43') {
  if (!dateStr || typeof dateStr !== 'string') return '';
  const normalized = String(dateStr).replace(/\./g, '-').trim();
  const [y, m, d] = normalized.split(/[-/]/);
  if (!y || !m) return dateStr;
  const month = m.padStart(2, '0');
  const day = (d && d.padStart(2, '0')) || '01';
  return `${y}년 ${month}월 ${day}일 ${timeStr}`;
}

const config = {};

/** 태그 3개 이상 보장 (field 슬래시 분리 + config.tagFallbacks로 보강) */
function ensureTags(field, title, defaultTags = []) {
  const fromField = field ? String(field).split(/[/,]/).map((s) => s.trim()).filter(Boolean) : [];
  const combined = [...new Set([...fromField, ...defaultTags])];
  if (combined.length >= 3) return combined.slice(0, 5);
  const fallbacks = Array.isArray(config.tagFallbacks) ? config.tagFallbacks : ['협력', '연구', '플랫폼', '검증', '구축'];
  for (const f of fallbacks) {
    if (combined.length >= 3) break;
    if (!combined.includes(f)) combined.push(f);
  }
  return combined.slice(0, 5);
}

/** 목업용 담당자명 (직책 포함) - alliance-list-config.mockLeaders 인덱스별 */
function getMockLeaderName(index) {
  const leaders = config.mockLeaders ?? [];
  return leaders[index % leaders.length] || '';
}

/**
 * PoC/협의체 목업을 Alliance 목록 카드 형태로 변환 (council/poc 목록과 JSON 병합 시 활용)
 * @internal
 */
// eslint-disable-next-line no-unused-vars
function mapPocToAllianceCard(item, index) {
  const created = String(item.createdDate || '');
  let openedAtSort = created.replace(/\./g, '-').trim();
  if (openedAtSort.length === 7) openedAtSort += '-01';
  else if (openedAtSort.length < 10) openedAtSort = created || '';
  const title = cleanAllianceTitle(item.projectName || item.name || '');
  const tags = ensureTags(item.field, title);
  return {
    id: item.id,
    title,
    leaderName: getMockLeaderName(index),
    org: item.owner || '',
    description: item.summary || '',
    participantCount: item.memberCount ?? null,
    openedAt: formatOpenedAt(item.createdDate),
    openedAtSort: openedAtSort.length >= 10 ? openedAtSort : `${openedAtSort}-01`,
    updatedAt: formatUpdatedAt(config.defaultUpdatedAt, config.defaultUpdatedAtTime),
    tags,
    statusBadge: 'Pre-Alliance',
    spaceType: item.spaceType || DEFAULT_SPACE_TYPE,
    thumb: item.thumb || '',
    newCount: (config.newCountSequence ?? [2, 128, null])[index % 3]
  };
}

// eslint-disable-next-line no-unused-vars
function mapCouncilToAllianceCard(item, spaceType, index, totalIndex) {
  const period = item.period || '';
  const m = period.match(/(\d{4})\.?\s*(\d{1,2})/);
  const openedAtSort = m ? `${m[1]}-${m[2].padStart(2, '0')}-01` : '';
  const statusMap = config.statusBadgeMap ?? {};
  const progressCd = normalizeCouncilProgressCd(item);
  const title = cleanAllianceTitle(item.projectName || item.name || '');
  const openDateStr = m ? `${m[1]}.${m[2]}.01` : '';
  const tags = ensureTags(item.field, title);
  const newCountSeq = config.newCountSequence ?? [2, 128, null];
  // 시안: 등록 심사 상태별 뱃지·라우팅 (목업: 첫 건 pending, 둘째 건 rejected, 나머지 approved)
  const spaceStatusByIndex = totalIndex === 0 ? 'pending' : totalIndex === 1 ? 'rejected' : 'approved';
  const statusBadgeLabel =
    spaceStatusByIndex === 'pending'
      ? '승인 대기'
      : spaceStatusByIndex === 'rejected'
        ? '반려'
        : statusMap[progressCd] || DEFAULT_COUNCIL_BADGE_BY_PROGRESS[progressCd] || 'Alliance';
  const listLifecycleCd =
    progressCd === ALLIANCE_MOCK_COUNCIL_PROGRESS.ENDED
      ? ALLIANCE_LIST_LIFECYCLE.ENDED
      : progressCd === ALLIANCE_MOCK_COUNCIL_PROGRESS.CLOSED
        ? ALLIANCE_LIST_LIFECYCLE.CLOSED
        : undefined;
  return {
    id: item.id,
    title,
    leaderName: getMockLeaderName(index),
    org: item.org || '',
    description: item.projectName || '',
    participantCount: item.memberCount ?? null,
    openedAt: formatOpenedAt(openDateStr) || period,
    openedAtSort,
    updatedAt: formatUpdatedAt(config.defaultUpdatedAt, config.defaultUpdatedAtTime),
    tags,
    statusBadge: statusBadgeLabel,
    spaceStatus: spaceStatusByIndex,
    spaceType: item.spaceType || spaceType,
    thumb: item.thumb || '',
    newCount: newCountSeq[(index + 2) % 3],
    ...(listLifecycleCd ? { listLifecycleCd } : {})
  };
}

/**
 * Alliance Space 목록 (참여한 / 초대 받은)
 * @deprecated 동기 목업 목록. 서버 API는 fetchAllianceList() 사용.
 */
export function getAllianceList() {
  return { participated: [], invited: [] };
}

/** 카드 분류 태그용: DB space_type_cd와 별도로 연결 소스(source_type_cd)가 오픈 커뮤니티면 태그를 구분한다. */
function mapBackendSpaceType(spaceTypeCd, sourceUid, sourceTypeCd) {
  if (spaceTypeCd === 'RESEARCH_PROJECT') return 'research';
  if (spaceTypeCd === 'GENERAL') return 'unspecified';
  const uid = sourceUid != null ? Number(sourceUid) : NaN;
  const src = String(sourceTypeCd || '').toUpperCase();
  if (src === 'COMMUNITY_PROJECT' && Number.isFinite(uid) && uid > 0) return 'community-project';
  if (spaceTypeCd === 'TECH_DEMAND' && (!Number.isFinite(uid) || uid <= 0)) return 'unspecified';
  return 'tech-demand';
}

function mapBackendSpaceStatus(approvalStatusCd) {
  const status = String(approvalStatusCd || '').toUpperCase();
  if (status === ALLIANCE_APPROVAL_STATUS.PENDING) return 'pending';
  if (status === ALLIANCE_APPROVAL_STATUS.REJECTED) return 'rejected';
  return 'approved';
}

function mapBackendStatusBadge(item) {
  const status = mapBackendSpaceStatus(item?.approvalStatusCd);
  if (status === 'pending') return '승인 대기';
  if (status === 'rejected') return '반려';
  return item?.stageCd === ALLIANCE_STAGE_CD.PRE ? 'Pre-Alliance' : 'Alliance';
}

function formatListDate(dateStr) {
  if (!dateStr || typeof dateStr !== 'string') return '';
  const normalized = String(dateStr).trim().replace(/\./g, '-');
  const match = normalized.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return dateStr;
  return `${match[1]}년 ${match[2]}월 ${match[3]}일`;
}

function formatListDateTime(dateStr) {
  if (!dateStr || typeof dateStr !== 'string') return '';
  const normalized = String(dateStr).trim().replace(/\./g, '-');
  const match = normalized.match(/^(\d{4})-(\d{2})-(\d{2})\s+(\d{2}):(\d{2})/);
  if (!match) return formatListDate(dateStr);
  return `${match[1]}년 ${match[2]}월 ${match[3]}일 ${match[4]}:${match[5]}`;
}

function mapParticipantMemberRow(m) {
  if (!m || typeof m !== 'object') return null;
  return {
    userUid: m.userUid,
    userNm: m.userNm || '',
    userId: m.userId || '',
    authorCd: m.authorCd || '',
    memberRoleCd: m.memberRoleCd || '',
    joinStatusCd: m.joinStatusCd || '',
    accessSuspendedYn: m.accessSuspendedYn || 'N',
  };
}

function mapBackendAllianceCard(item) {
  const regDt = item?.regDt || '';
  const updtDt = item?.updtDt || regDt;
  const thumbnailFileUid = item?.thumbnailFileUid;
  const rawMembers = item?.participantMembers;
  const participantMembers = Array.isArray(rawMembers)
    ? rawMembers.map(mapParticipantMemberRow).filter(Boolean)
    : null;
  const endAtYmd = item?.endAt != null ? String(item.endAt).trim().slice(0, 10) : '';
  const listLifecycleCd = listLifecycleCdFromAllianceEndAt(item?.endAt);
  return {
    id: String(item?.uid ?? ''),
    title: item?.spaceNm || '',
    leaderName: item?.ownerNm || '',
    regUid: item?.ownerUid ?? null,
    leaderUid: item?.ownerUid ?? null,
    leaderUserId: item?.ownerUserId || '',
    org: item?.ownerOrg || '',
    description: item?.spaceDesc || '',
    participantCount: item?.participantCount ?? null,
    /** my-summary에 포함된 툴팁용 멤버(있으면 hover 시 추가 API 호출 안 함) */
    participantMembers,
    openedAt: formatListDate(regDt),
    openedAtSort: regDt ? String(regDt).trim().replace(/\./g, '-').slice(0, 10) : '',
    updatedAt: formatListDateTime(updtDt),
    /** 목록 날짜 필터용 (YYYY-MM-DD) */
    updatedAtSort: updtDt ? String(updtDt).trim().replace(/\./g, '-').slice(0, 10) : '',
    tags: [],
    statusBadge: mapBackendStatusBadge(item),
    spaceStatus: mapBackendSpaceStatus(item?.approvalStatusCd),
    spaceType: mapBackendSpaceType(item?.spaceTypeCd, item?.sourceUid, item?.sourceTypeCd),
    sourceName: item?.sourceNm || '',
    sourceDetailUrl: item?.sourceDetailUrl || null,
    /** 연구사업 사전협력 화면 등에서 동일 공고 스페이스만 필터할 때 사용 */
    sourceTypeCd: item?.sourceTypeCd || '',
    sourceUid: item?.sourceUid != null ? item.sourceUid : null,
    thumb: thumbnailFileUid ? `/api/glow/files/viewByUid/${thumbnailFileUid}` : '',
    newCount: null,
    /** 참여 탭: 본인 멤버 행이 접근 중지된 경우 Y */
    myAccessSuspendedYn: item?.myAccessSuspendedYn || 'N',
    operationStatusCd: normalizeAllianceOperationStatusCd(item?.operationStatusCd),
    adminClosedYn: String(item?.adminClosedYn || 'N').toUpperCase() === 'Y' ? 'Y' : 'N',
    ...(endAtYmd && /^\d{4}-\d{2}-\d{2}$/.test(endAtYmd) ? { endAt: endAtYmd } : {}),
    ...(listLifecycleCd ? { listLifecycleCd } : {}),
  };
}

export async function fetchAllianceList() {
  const res = await Http.get('/api/alli/spaces/my-summary');
  return {
    participated: (res?.participated ?? []).map(mapBackendAllianceCard),
    invited: (res?.invited ?? []).map(mapBackendAllianceCard)
  };
}

/** 백엔드 cetus.bean.Page: list, totalElements, number(1-based), size, totalPages */
function parseCetusPage(res, mapRow) {
  const raw = res?.list;
  const list = Array.isArray(raw) ? raw : [];
  return {
    list: typeof mapRow === 'function' ? list.map(mapRow) : list,
    totalElements: Number(res?.totalElements ?? 0),
    number: Number(res?.number ?? 1),
    size: Number(res?.size ?? 0),
    totalPages: Number(res?.totalPages ?? 0),
  };
}

/**
 * 참여한 Alliance Space — 서버 페이징 (백엔드 Pageable: pageNumber 1-based, size)
 */
export async function fetchAllianceParticipatedPage(params = {}) {
  const pageNumber = params.pageNumber ?? 1;
  const size = params.size ?? 10;
  const res = await Http.get('/api/alli/spaces/my-participated', {
    params: { pageNumber, size },
  });
  return parseCetusPage(res, mapBackendAllianceCard);
}

/**
 * 초대받은 Alliance Space — 서버 페이징
 */
export async function fetchAllianceInvitedPage(params = {}) {
  const pageNumber = params.pageNumber ?? 1;
  const size = params.size ?? 10;
  const res = await Http.get('/api/alli/spaces/my-invited', {
    params: { pageNumber, size },
  });
  return parseCetusPage(res, mapBackendAllianceCard);
}

function mapAdminAllianceItem(item) {
  const regDt = item?.regDt || '';
  const updtDt = item?.updtDt || regDt;
  const thumbnailFileUid = item?.thumbnailFileUid;
  const endAtYmd = item?.endAt != null ? String(item.endAt).trim().slice(0, 10) : '';
  const listLifecycleCd = listLifecycleCdFromAllianceEndAt(item?.endAt);
  return {
    id: String(item?.uid ?? ''),
    title: item?.spaceNm || '',
    leaderName: item?.ownerNm || '',
    regUid: item?.ownerUid ?? null,
    leaderUid: item?.ownerUid ?? null,
    leaderUserId: item?.ownerUserId || '',
    org: item?.ownerOrg || '',
    description: item?.spaceDesc || '',
    participantCount: item?.participantCount ?? null,
    openedAt: formatListDate(regDt),
    openedAtSort: regDt ? String(regDt).trim().replace(/\./g, '-').slice(0, 10) : '',
    updatedAt: formatListDateTime(updtDt),
    /** 목록 날짜 필터용 (YYYY-MM-DD) */
    updatedAtSort: updtDt ? String(updtDt).trim().replace(/\./g, '-').slice(0, 10) : '',
    tags: [],
    statusBadge: mapBackendStatusBadge(item),
    spaceStatus: mapBackendSpaceStatus(item?.approvalStatusCd),
    spaceType: mapBackendSpaceType(item?.spaceTypeCd, item?.sourceUid, item?.sourceTypeCd),
    sourceName: item?.sourceNm || '',
    /** 연구과제일 때 NTIS 상세 URL (roRndUid 포함). 기술수요는 null */
    sourceDetailUrl: item?.sourceDetailUrl || null,
    thumb: thumbnailFileUid ? `/api/glow/files/viewByUid/${thumbnailFileUid}` : '',
    newCount: null,
    approvalStatusCd: item?.approvalStatusCd || '',
    sourceTypeCd: item?.sourceTypeCd || '',
    sourceUid: item?.sourceUid || null,
    ownerUid: item?.ownerUid || null,
    regDt,
    updtDt,
    operationStatusCd: normalizeAllianceOperationStatusCd(item?.operationStatusCd),
    adminClosedYn: String(item?.adminClosedYn || 'N').toUpperCase() === 'Y' ? 'Y' : 'N',
    ...(endAtYmd && /^\d{4}-\d{2}-\d{2}$/.test(endAtYmd) ? { endAt: endAtYmd } : {}),
    ...(listLifecycleCd ? { listLifecycleCd } : {}),
  };
}

export async function fetchAdminAllianceList(params = {}) {
  const query = new URLSearchParams();
  const pageNumber = params.pageNumber ?? params.page ?? 1;
  query.set('pageNumber', String(pageNumber));
  query.set('size', String(params.size ?? 10));
  if (params.approvalStatusCd) query.set('approvalStatusCd', params.approvalStatusCd);
  if (params.stageCd) query.set('stageCd', params.stageCd);
  if (params.operationStatusCd) query.set('operationStatusCd', params.operationStatusCd);
  if (params.adminClosedYn) query.set('adminClosedYn', params.adminClosedYn);
  if (params.searchKeyword) query.set('searchKeyword', params.searchKeyword);
  const res = await Http.get(`/api/alli/spaces?${query.toString()}`);
  const parsed = parseCetusPage(res);
  const list = parsed.list;
  return {
    items: list.map(mapAdminAllianceItem),
    totalElements: parsed.totalElements,
  };
}

export async function approveAllianceSpace(spaceUid) {
  await Http.post(`/api/alli/spaces/${spaceUid}/approve`);
}

export async function rejectAllianceSpace(spaceUid, rejectReason) {
  await Http.post(`/api/alli/spaces/${spaceUid}/reject`, { rejectReason });
}

export async function hasMyAllianceSpace(sourceTypeCd, sourceUid) {
  if (!sourceTypeCd || !sourceUid) return false;
  const res = await Http.get(
    `/api/alli/spaces/my?sourceTypeCd=${encodeURIComponent(sourceTypeCd)}&sourceUid=${encodeURIComponent(sourceUid)}&pageNumber=1&size=1`
  );
  return Number(res?.totalElements || 0) > 0;
}

/**
 * ID로 스페이스 한 건 조회 (목록 participated + invited에서 검색)
 * 대시보드 등에서 단일 소스로 name, spaceType, statusBadge(Pre/Alliance) 사용
 * @param {string} spaceId
 * @returns {{ id, title, spaceType, statusBadge, spaceStatus, ... } | null}
 */
export function getSpaceById(spaceId) {
  // NOTE: 과거 목업 목록(participated/invited)에서 동기 탐색하던 함수.
  // 현재는 fetchSpaceById(spaceId)로 서버 조회한다.
  return null;
}

export async function patchAllianceOperationStatus(spaceId, operationStatusCd) {
  if (!spaceId) throw new Error('spaceId is required');
  await Http.patch(`/api/alli/spaces/${encodeURIComponent(spaceId)}/operation-status`, {
    operationStatusCd,
  });
}

/** GLOW 관리자 전용: Space 폐쇄(Y) / 해제(N). 운영 상태 종료와 별개. */
export async function patchAllianceAdminClosure(spaceId, adminClosedYn) {
  if (!spaceId) throw new Error('spaceId is required');
  const yn = String(adminClosedYn || '').trim().toUpperCase();
  if (yn !== 'Y' && yn !== 'N') throw new Error('adminClosedYn must be Y or N');
  await Http.patch(`/api/alli/spaces/${encodeURIComponent(spaceId)}/admin-closure`, {
    adminClosedYn: yn,
  });
}

/** GLOW 관리자 전용: 관리자 폐쇄(ADMIN_CLOSED_YN=Y) 상태 Space 소프트 삭제 */
export async function deleteAdminClosedAllianceSpace(spaceId) {
  if (!spaceId) throw new Error('spaceId is required');
  await Http.delete(`/api/alli/spaces/${encodeURIComponent(spaceId)}`);
}

export async function fetchSpaceById(spaceId) {
  if (!spaceId) return null;
  let res;
  try {
    res = await Http.get(`/api/alli/spaces/${spaceId}`);
  } catch (e) {
    const msg = String(e?.response?.data?.message || '').toLowerCase();
    const status = Number(e?.response?.status || 0);
    if (shouldRethrowSpaceFetchError(status, msg)) {
      throw e;
    }
    return null;
  }
  if (!res) return null;

  const regDt = res?.regDt || '';
  const updtDt = res?.updtDt || regDt;
  const thumbnailFileUid = res?.thumbnailFileUid;
  const endAtYmd = res?.endAt != null ? String(res.endAt).trim().slice(0, 10) : '';
  const listLifecycleCd = listLifecycleCdFromAllianceEndAt(res?.endAt);

  return {
    id: String(res?.uid ?? spaceId),
    title: res?.spaceNm || '',
    leaderName: res?.ownerNm || '',
    regUid: res?.ownerUid ?? null,
    leaderUid: res?.ownerUid ?? null,
    leaderUserId: res?.ownerUserId || '',
    org: res?.ownerOrg || '',
    description: res?.spaceDesc || '',
    participantCount: res?.participantCount ?? null,
    openedAt: formatListDate(regDt),
    openedAtSort: regDt ? String(regDt).trim().replace(/\./g, '-').slice(0, 10) : '',
    updatedAt: formatListDateTime(updtDt),
    /** 목록 날짜 필터용 (YYYY-MM-DD) */
    updatedAtSort: updtDt ? String(updtDt).trim().replace(/\./g, '-').slice(0, 10) : '',
    tags: [],
    statusBadge: mapBackendStatusBadge(res),
    spaceStatus: mapBackendSpaceStatus(res?.approvalStatusCd),
    spaceType: mapBackendSpaceType(res?.spaceTypeCd, res?.sourceUid, res?.sourceTypeCd),
    sourceName: res?.sourceNm || '',
    sourceDetailUrl: res?.sourceDetailUrl || null,
    sourceBudgetText: res?.sourceBudgetText != null ? String(res.sourceBudgetText).trim() : '',
    sourceBriefSummary: res?.sourceBriefSummary != null ? String(res.sourceBriefSummary).trim() : '',
    sourceHostOrgText: res?.sourceHostOrgText != null ? String(res.sourceHostOrgText).trim() : '',
    sourcePeriodFrom: res?.sourcePeriodFrom != null ? String(res.sourcePeriodFrom).trim() : '',
    sourcePeriodTo: res?.sourcePeriodTo != null ? String(res.sourcePeriodTo).trim() : '',
    sourceDeadlineDate: res?.sourceDeadlineDate != null ? String(res.sourceDeadlineDate).trim() : '',
    thumb: thumbnailFileUid ? `/api/glow/files/viewByUid/${thumbnailFileUid}` : '',
    newCount: null,
    approvalStatusCd: res?.approvalStatusCd || '',
    sourceTypeCd: res?.sourceTypeCd || '',
    sourceUid: res?.sourceUid || null,
    ownerUid: res?.ownerUid || null,
    regDt,
    updtDt,
    operationStatusCd: normalizeAllianceOperationStatusCd(res?.operationStatusCd),
    adminClosedYn: String(res?.adminClosedYn || 'N').toUpperCase() === 'Y' ? 'Y' : 'N',
    ...(endAtYmd && /^\d{4}-\d{2}-\d{2}$/.test(endAtYmd) ? { endAt: endAtYmd } : {}),
    ...(listLifecycleCd ? { listLifecycleCd } : {}),
  };
}

export async function fetchAllianceTransitionInfo(spaceId) {
  if (!spaceId) return null;
  return Http.get(`/api/alli/spaces/${spaceId}/transition`);
}

export async function transitionAllianceSpace(spaceId, payload) {
  if (!spaceId) throw new Error('spaceId is required');
  return Http.post(`/api/alli/spaces/${spaceId}/transition`, payload);
}

/**
 * 위원회/이사회 목록
 * @param {string} [spaceType] - 'tech-demand' | 'research'
 * @returns Array
 */
export function getCouncilList(spaceType) {
  return [];
}

// ----- 마일스톤 (DB 연동 시 개별 엔드포인트로 교체 가능) -----

/**
 * 마일스톤 프로젝트 목록 (카드 목록용)
 * @param {string} [spaceType]
 * @returns Array<{ id, title, org, person, progress, statusTag, thumb, ... }>
 */
export function getMilestoneProjects(spaceType = DEFAULT_SPACE_TYPE) {
  const data = getSpaceData(spaceType);
  return data?.milestoneTabItems ?? [];
}

/**
 * 마일스톤 프로젝트 1건 (id 기준)
 * @param {string} [spaceType]
 * @param {number|string} projectId
 * @returns object | null
 */
export function getMilestoneProjectById(spaceType, projectId) {
  const data = getSpaceData(spaceType);
  const byId = data?.milestoneDetail?.projectById ?? {};
  const id = Number(projectId);
  return byId[id] ?? byId[String(projectId)] ?? byId[1] ?? null;
}

/**
 * 마일스톤 상세 1건 (게시글 id 기준)
 * @param {string} [spaceType]
 * @param {number|string} detailId
 * @returns object | null
 */
export function getMilestoneDetailById(spaceType, detailId) {
  const data = getSpaceData(spaceType);
  const byId = data?.milestoneDetail?.detailById ?? {};
  const id = Number(detailId);
  return byId[id] ?? byId[String(detailId)] ?? null;
}

/**
 * 마일스톤 상세 목록 (특정 프로젝트 하위 게시글 목록, 목록 페이지용)
 * @param {string} [spaceType]
 * @param {number|string} projectId
 * @returns Array<{ id, title, summary, tags, replies, views, writtenAt, authorName, org, ... }>
 */
function stripHtml(html) {
  if (typeof html !== 'string') return '';
  return html.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
}

export function getMilestoneDetailsByProject(spaceType, projectId) {
  const data = getSpaceData(spaceType);
  const byId = data?.milestoneDetail?.detailById ?? {};
  const pid = Number(projectId);
  const list = Object.values(byId).filter((d) => Number(d?.projectId) === pid);
  return list.map((d) => {
    const raw = d.body ? stripHtml(d.body) : (d.summary ?? '');
    const summary = raw.length > 80 ? `${raw.slice(0, 80)}...` : raw;
    return {
      id: d.id,
      title: d.title,
      summary,
      tags: d.tags ?? [],
      replies: d.replies ?? 0,
      views: d.views ?? 0,
      writtenAt: d.writtenAt ?? d.updatedAt ?? '',
      authorInitial: (d.authorName || '').charAt(0) || '?',
      authorName: d.authorName ?? '',
      org: d.org ?? d.authorOrg ?? ''
    };
  });
}

/**
 * 마일스톤 댓글 목록 (상세 id 기준)
 * @param {string} [spaceType]
 * @param {number|string} detailId
 * @returns Array<{ id, writer, date, content }>
 */
export function getMilestoneRepliesByDetailId(spaceType, detailId) {
  const data = getSpaceData(spaceType);
  const byDetail = data?.milestoneDetail?.repliesByDetailId ?? {};
  const list = byDetail[String(detailId)] ?? [];
  return Array.isArray(list) ? [...list] : [];
}

/**
 * 마일스톤 영역 전체 데이터 (projectById, detailById, repliesByDetailId) - 한 번에 넘겨줄 때용
 * @param {string} [spaceType]
 * @returns { projectById: {}, detailById: {}, repliesByDetailId: {} }
 */
export function getMilestoneDetailMaps(spaceType = DEFAULT_SPACE_TYPE) {
  const data = getSpaceData(spaceType);
  const md = data?.milestoneDetail ?? {};
  return {
    projectById: md.projectById ?? {},
    detailById: md.detailById ?? {},
    repliesByDetailId: md.repliesByDetailId ?? {}
  };
}
